import { useMutation, useQuery } from "@apollo/client/react";
import { Code, Loader, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { ResultOf, graphql } from "gql.tada";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import { CreationForm } from "../../components/form/CreationForm";

const EMAIL_TEMPLATE_CONFIGURATIONS_QUERY = graphql(`
  query EmailTemplateConfigurations {
    emailTemplateConfigurations {
      name
      description
      triggerEvent
      availableVariables {
        description
        name
      }
    }
  }
`);

const EMAIL_TEMPLATE_QUERY = graphql(`
  query EmailTemplate($id: ID!) {
    emailTemplate(id: $id) {
      id
      name
      contents
      title
      triggerEvent
      default
    }
  }
`);

const EMAIL_TEMPLATE_UPDATE_MUTATION = graphql(`
  mutation EmailTemplateUpdate($id: ID!, $input: EmailTemplateUpdateInput!) {
    emailTemplateUpdate(id: $id, input: $input) {
      id
    }
  }
`);

const EMAIL_TEMPLATE_CREATE_MUTATION = graphql(`
  mutation EmailTemplateCreate($input: EmailTemplateCreateInput!) {
    emailTemplateCreate(input: $input) {
      id
    }
  }
`);

const templates: Partial<
  Record<
    ResultOf<
      typeof EMAIL_TEMPLATE_CONFIGURATIONS_QUERY
    >["emailTemplateConfigurations"][number]["triggerEvent"],
    {
      title: string;
      contents: string;
    }
  >
> = {
  EVENT_REGISTRATION_COMPLETED: {
    title: "Your tickets are available for the event {{event_name}}!",
    contents: `
<!-- Source: https://github.com/leemunroe/responsive-html-email-template/blob/master/email.html -->
<!doctype html>
<html lang="en">
  <head>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
    <title>Your tickets are available for the event {{event_name}}!</title>
    <style media="all" type="text/css">
    /* -------------------------------------
    GLOBAL RESETS
------------------------------------- */
    
    body {
      font-family: Helvetica, sans-serif;
      -webkit-font-smoothing: antialiased;
      font-size: 16px;
      line-height: 1.3;
      -ms-text-size-adjust: 100%;
      -webkit-text-size-adjust: 100%;
    }
    
    table {
      border-collapse: separate;
      mso-table-lspace: 0pt;
      mso-table-rspace: 0pt;
      width: 100%;
    }
    
    table td {
      font-family: Helvetica, sans-serif;
      font-size: 16px;
      vertical-align: top;
    }
    /* -------------------------------------
    BODY & CONTAINER
------------------------------------- */
    
    body {
      background-color: #f4f5f6;
      margin: 0;
      padding: 0;
    }
    
    .body {
      background-color: #f4f5f6;
      width: 100%;
    }
    
    .container {
      margin: 0 auto !important;
      max-width: 600px;
      padding: 0;
      padding-top: 24px;
      width: 600px;
    }
    
    .content {
      box-sizing: border-box;
      display: block;
      margin: 0 auto;
      max-width: 600px;
      padding: 0;
    }
    /* -------------------------------------
    HEADER, FOOTER, MAIN
------------------------------------- */
    
    .main {
      background: #ffffff;
      border: 1px solid #eaebed;
      border-radius: 16px;
      width: 100%;
    }
    
    .wrapper {
      box-sizing: border-box;
      padding: 24px;
    }
    
    .footer {
      clear: both;
      padding-top: 24px;
      text-align: center;
      width: 100%;
    }
    
    .footer td,
    .footer p,
    .footer span,
    .footer a {
      color: #9a9ea6;
      font-size: 16px;
      text-align: center;
    }
    /* -------------------------------------
    TYPOGRAPHY
------------------------------------- */
    
    p {
      font-family: Helvetica, sans-serif;
      font-size: 16px;
      font-weight: normal;
      margin: 0;
      margin-bottom: 16px;
    }
    
    a {
      color: #0867ec;
      text-decoration: underline;
    }
    /* -------------------------------------
    BUTTONS
------------------------------------- */
    
    .btn {
      box-sizing: border-box;
      min-width: 100% !important;
      width: 100%;
    }
    
    .btn > tbody > tr > td {
      padding-bottom: 16px;
    }
    
    .btn table {
      width: auto;
    }
    
    .btn table td {
      background-color: #ffffff;
      border-radius: 4px;
      text-align: center;
    }
    
    .btn a {
      background-color: #ffffff;
      border: solid 2px #0867ec;
      border-radius: 4px;
      box-sizing: border-box;
      color: #0867ec;
      cursor: pointer;
      display: inline-block;
      font-size: 16px;
      font-weight: bold;
      margin: 0;
      padding: 12px 24px;
      text-decoration: none;
      text-transform: capitalize;
    }
    
    .btn-primary table td {
      background-color: #0867ec;
    }
    
    .btn-primary a {
      background-color: #0867ec;
      border-color: #0867ec;
      color: #ffffff;
    }
    
    @media all {
      .btn-primary table td:hover {
        background-color: #ec0867 !important;
      }
      .btn-primary a:hover {
        background-color: #ec0867 !important;
        border-color: #ec0867 !important;
      }
    }
    
    /* -------------------------------------
    OTHER STYLES THAT MIGHT BE USEFUL
------------------------------------- */
    
    .last {
      margin-bottom: 0;
    }
    
    .first {
      margin-top: 0;
    }
    
    .align-center {
      text-align: center;
    }
    
    .align-right {
      text-align: right;
    }
    
    .align-left {
      text-align: left;
    }
    
    .text-link {
      color: #0867ec !important;
      text-decoration: underline !important;
    }
    
    .clear {
      clear: both;
    }
    
    .mt0 {
      margin-top: 0;
    }
    
    .mb0 {
      margin-bottom: 0;
    }
    
    .preheader {
      color: transparent;
      display: none;
      height: 0;
      max-height: 0;
      max-width: 0;
      opacity: 0;
      overflow: hidden;
      mso-hide: all;
      visibility: hidden;
      width: 0;
    }
    
    .powered-by a {
      text-decoration: none;
    }
    
    /* -------------------------------------
    RESPONSIVE AND MOBILE FRIENDLY STYLES
------------------------------------- */
    
    @media only screen and (max-width: 640px) {
      .main p,
      .main td,
      .main span {
        font-size: 16px !important;
      }
      .wrapper {
        padding: 8px !important;
      }
      .content {
        padding: 0 !important;
      }
      .container {
        padding: 0 !important;
        padding-top: 8px !important;
        width: 100% !important;
      }
      .main {
        border-left-width: 0 !important;
        border-radius: 0 !important;
        border-right-width: 0 !important;
      }
      .btn table {
        max-width: 100% !important;
        width: 100% !important;
      }
      .btn a {
        font-size: 16px !important;
        max-width: 100% !important;
        width: 100% !important;
      }
    }
    /* -------------------------------------
    PRESERVE THESE STYLES IN THE HEAD
------------------------------------- */
    
    @media all {
      .ExternalClass {
        width: 100%;
      }
      .ExternalClass,
      .ExternalClass p,
      .ExternalClass span,
      .ExternalClass font,
      .ExternalClass td,
      .ExternalClass div {
        line-height: 100%;
      }
      .apple-link a {
        color: inherit !important;
        font-family: inherit !important;
        font-size: inherit !important;
        font-weight: inherit !important;
        line-height: inherit !important;
        text-decoration: none !important;
      }
      #MessageViewBody a {
        color: inherit;
        text-decoration: none;
        font-size: inherit;
        font-family: inherit;
        font-weight: inherit;
        line-height: inherit;
      }
    }
    </style>
  </head>
  <body>
    <table role="presentation" border="0" cellpadding="0" cellspacing="0" class="body">
      <tr>
        <td>&nbsp;</td>
        <td class="container">
          <div class="content">

            <!-- START CENTERED WHITE CONTAINER -->
            <span class="preheader">Your tickets are available for the event {{event_name}}!</span>
            <table role="presentation" border="0" cellpadding="0" cellspacing="0" class="main">

              <!-- START MAIN CONTENT AREA -->
              <tr>
                <td class="wrapper">
                  <p>Hi {{username}}</p>
                  <p>Your tickets are available for the event {{event_name}}!</p>
                  <table role="presentation" border="0" cellpadding="0" cellspacing="0" class="btn btn-primary">
                    <tbody>
                      <tr>
                        <td align="left">
                          <table role="presentation" border="0" cellpadding="0" cellspacing="0">
                            <tbody>
                              <tr>
                                <td> <a href="https://my-platform-url.com/events/{{event_id}}/reservation" target="_blank">Access your reservation</a> </td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <p>Your ticket QRCode:</p>
                  <img src="{{qrcode_data_url}}" alt="Ticket QRCode" />
                </td>
              </tr>

              <!-- END MAIN CONTENT AREA -->
              </table>

            <!-- START FOOTER -->
            <div class="footer">
              <table role="presentation" border="0" cellpadding="0" cellspacing="0">
                <tr>
                  <td class="content-block">
                    <span class="apple-link">Your copyright here</span>
                  </td>
                </tr>
              </table>
            </div>

            <!-- END FOOTER -->
            
<!-- END CENTERED WHITE CONTAINER --></div>
        </td>
        <td>&nbsp;</td>
      </tr>
    </table>
  </body>
</html>`,
  },
  EVENT_REGISTRATION_ADMIN_UPDATE_STATUS_PENDING: {
    title: "Your payment session has started for the event {{event_name}}!",
    contents: `
<!-- Source: https://github.com/leemunroe/responsive-html-email-template/blob/master/email.html -->
<!doctype html>
<html lang="en">
  <head>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
    <title>Your payment session has started for the event {{event_name}}!</title>
    <style media="all" type="text/css">
    /* -------------------------------------
    GLOBAL RESETS
------------------------------------- */
    
    body {
      font-family: Helvetica, sans-serif;
      -webkit-font-smoothing: antialiased;
      font-size: 16px;
      line-height: 1.3;
      -ms-text-size-adjust: 100%;
      -webkit-text-size-adjust: 100%;
    }
    
    table {
      border-collapse: separate;
      mso-table-lspace: 0pt;
      mso-table-rspace: 0pt;
      width: 100%;
    }
    
    table td {
      font-family: Helvetica, sans-serif;
      font-size: 16px;
      vertical-align: top;
    }
    /* -------------------------------------
    BODY & CONTAINER
------------------------------------- */
    
    body {
      background-color: #f4f5f6;
      margin: 0;
      padding: 0;
    }
    
    .body {
      background-color: #f4f5f6;
      width: 100%;
    }
    
    .container {
      margin: 0 auto !important;
      max-width: 600px;
      padding: 0;
      padding-top: 24px;
      width: 600px;
    }
    
    .content {
      box-sizing: border-box;
      display: block;
      margin: 0 auto;
      max-width: 600px;
      padding: 0;
    }
    /* -------------------------------------
    HEADER, FOOTER, MAIN
------------------------------------- */
    
    .main {
      background: #ffffff;
      border: 1px solid #eaebed;
      border-radius: 16px;
      width: 100%;
    }
    
    .wrapper {
      box-sizing: border-box;
      padding: 24px;
    }
    
    .footer {
      clear: both;
      padding-top: 24px;
      text-align: center;
      width: 100%;
    }
    
    .footer td,
    .footer p,
    .footer span,
    .footer a {
      color: #9a9ea6;
      font-size: 16px;
      text-align: center;
    }
    /* -------------------------------------
    TYPOGRAPHY
------------------------------------- */
    
    p {
      font-family: Helvetica, sans-serif;
      font-size: 16px;
      font-weight: normal;
      margin: 0;
      margin-bottom: 16px;
    }
    
    a {
      color: #0867ec;
      text-decoration: underline;
    }
    /* -------------------------------------
    BUTTONS
------------------------------------- */
    
    .btn {
      box-sizing: border-box;
      min-width: 100% !important;
      width: 100%;
    }
    
    .btn > tbody > tr > td {
      padding-bottom: 16px;
    }
    
    .btn table {
      width: auto;
    }
    
    .btn table td {
      background-color: #ffffff;
      border-radius: 4px;
      text-align: center;
    }
    
    .btn a {
      background-color: #ffffff;
      border: solid 2px #0867ec;
      border-radius: 4px;
      box-sizing: border-box;
      color: #0867ec;
      cursor: pointer;
      display: inline-block;
      font-size: 16px;
      font-weight: bold;
      margin: 0;
      padding: 12px 24px;
      text-decoration: none;
      text-transform: capitalize;
    }
    
    .btn-primary table td {
      background-color: #0867ec;
    }
    
    .btn-primary a {
      background-color: #0867ec;
      border-color: #0867ec;
      color: #ffffff;
    }
    
    @media all {
      .btn-primary table td:hover {
        background-color: #ec0867 !important;
      }
      .btn-primary a:hover {
        background-color: #ec0867 !important;
        border-color: #ec0867 !important;
      }
    }
    
    /* -------------------------------------
    OTHER STYLES THAT MIGHT BE USEFUL
------------------------------------- */
    
    .last {
      margin-bottom: 0;
    }
    
    .first {
      margin-top: 0;
    }
    
    .align-center {
      text-align: center;
    }
    
    .align-right {
      text-align: right;
    }
    
    .align-left {
      text-align: left;
    }
    
    .text-link {
      color: #0867ec !important;
      text-decoration: underline !important;
    }
    
    .clear {
      clear: both;
    }
    
    .mt0 {
      margin-top: 0;
    }
    
    .mb0 {
      margin-bottom: 0;
    }
    
    .preheader {
      color: transparent;
      display: none;
      height: 0;
      max-height: 0;
      max-width: 0;
      opacity: 0;
      overflow: hidden;
      mso-hide: all;
      visibility: hidden;
      width: 0;
    }
    
    .powered-by a {
      text-decoration: none;
    }
    
    /* -------------------------------------
    RESPONSIVE AND MOBILE FRIENDLY STYLES
------------------------------------- */
    
    @media only screen and (max-width: 640px) {
      .main p,
      .main td,
      .main span {
        font-size: 16px !important;
      }
      .wrapper {
        padding: 8px !important;
      }
      .content {
        padding: 0 !important;
      }
      .container {
        padding: 0 !important;
        padding-top: 8px !important;
        width: 100% !important;
      }
      .main {
        border-left-width: 0 !important;
        border-radius: 0 !important;
        border-right-width: 0 !important;
      }
      .btn table {
        max-width: 100% !important;
        width: 100% !important;
      }
      .btn a {
        font-size: 16px !important;
        max-width: 100% !important;
        width: 100% !important;
      }
    }
    /* -------------------------------------
    PRESERVE THESE STYLES IN THE HEAD
------------------------------------- */
    
    @media all {
      .ExternalClass {
        width: 100%;
      }
      .ExternalClass,
      .ExternalClass p,
      .ExternalClass span,
      .ExternalClass font,
      .ExternalClass td,
      .ExternalClass div {
        line-height: 100%;
      }
      .apple-link a {
        color: inherit !important;
        font-family: inherit !important;
        font-size: inherit !important;
        font-weight: inherit !important;
        line-height: inherit !important;
        text-decoration: none !important;
      }
      #MessageViewBody a {
        color: inherit;
        text-decoration: none;
        font-size: inherit;
        font-family: inherit;
        font-weight: inherit;
        line-height: inherit;
      }
    }
    </style>
  </head>
  <body>
    <table role="presentation" border="0" cellpadding="0" cellspacing="0" class="body">
      <tr>
        <td>&nbsp;</td>
        <td class="container">
          <div class="content">

            <!-- START CENTERED WHITE CONTAINER -->
            <span class="preheader">Your tickets are available for the event {{event_name}}!</span>
            <table role="presentation" border="0" cellpadding="0" cellspacing="0" class="main">

              <!-- START MAIN CONTENT AREA -->
              <tr>
                <td class="wrapper">
                  <p>Hi {{username}}</p>
                  <p>Your payment session has started for the event {{event_name}}!</p>
                  <table role="presentation" border="0" cellpadding="0" cellspacing="0" class="btn btn-primary">
                    <tbody>
                      <tr>
                        <td align="left">
                          <table role="presentation" border="0" cellpadding="0" cellspacing="0">
                            <tbody>
                              <tr>
                                <td> <a href="https://my-platform-url.com/events/{{event_id}}/payment" target="_blank">Pay for your tickets now</a> </td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>

              <!-- END MAIN CONTENT AREA -->
              </table>

            <!-- START FOOTER -->
            <div class="footer">
              <table role="presentation" border="0" cellpadding="0" cellspacing="0">
                <tr>
                  <td class="content-block">
                    <span class="apple-link">Your copyright here</span>
                  </td>
                </tr>
              </table>
            </div>

            <!-- END FOOTER -->
            
<!-- END CENTERED WHITE CONTAINER --></div>
        </td>
        <td>&nbsp;</td>
      </tr>
    </table>
  </body>
</html>
  `,
  },
};

export const CreateOrUpdateEmailTemplate = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [selectedTriggerEvent, setSelectedTriggerEvent] = useState<
    | ResultOf<
        typeof EMAIL_TEMPLATE_CONFIGURATIONS_QUERY
      >["emailTemplateConfigurations"][number]["triggerEvent"]
    | null
  >(null);
  const { data, loading } = useQuery<any>(EMAIL_TEMPLATE_QUERY, {
    variables: { id: templateId ?? "" },
    skip: !templateId,
  });
  const [contents, setContents] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const { data: configurationsData, loading: configurationsLoading } =
    useQuery<any>(EMAIL_TEMPLATE_CONFIGURATIONS_QUERY);
  const [updateMutation] = useMutation<any>(EMAIL_TEMPLATE_UPDATE_MUTATION);
  const [createMutation] = useMutation<any>(EMAIL_TEMPLATE_CREATE_MUTATION);

  useEffect(() => {
    if (data?.emailTemplate) {
      setContents(data.emailTemplate.contents);
      setTitle(data.emailTemplate.title);
    }
  }, [data?.emailTemplate]);

  if (loading || configurationsLoading) {
    return <Loader />;
  }

  const selectedTriggerEventConfig =
    configurationsData?.emailTemplateConfigurations.find(
      (configuration: any) =>
        configuration.triggerEvent === selectedTriggerEvent ||
        configuration.triggerEvent === data?.emailTemplate?.triggerEvent,
    );
  return (
    <CreationForm
      title={templateId ? "Update email template" : "Create new email template"}
      submitText="Save"
      layout={{
        direction: "row",
        toolbar: "top",
        regions: [
          {
            key: "settings",
            label: "Template Settings",
            width: "280px",
            fields: [
              "name",
              "triggerEvent",
              "title",
              "default",
              "availableVariables",
            ],
          },
          {
            key: "editor",
            flex: 1,
            fields: ["contents"],
          },
          {
            key: "preview",
            flex: 1,
            fields: ["preview"],
          },
        ],
      }}
      schemaDefinition={{
        name: {
          type: "INPUT",
          label: "Name",
          yupConfig: yup.string().required(),
          defaultValue: data?.emailTemplate?.name,
          inputType: "text",
        },
        triggerEvent: {
          type: "SELECT",
          label: "Trigger event",
          yupConfig: yup.string().required(),
          options:
            configurationsData?.emailTemplateConfigurations.map(
              (configuration: any) => ({
                label: configuration.name,
                value: configuration.triggerEvent,
              }),
            ) ?? [],
          defaultValue: data?.emailTemplate?.triggerEvent,
          subtext: <Text>{selectedTriggerEventConfig?.description}</Text>,
          onChange(value) {
            if (value === data?.emailTemplate?.triggerEvent) {
              return;
            }
            setSelectedTriggerEvent(value as any);
            setContents(
              templates[
                value as ResultOf<
                  typeof EMAIL_TEMPLATE_CONFIGURATIONS_QUERY
                >["emailTemplateConfigurations"][number]["triggerEvent"]
              ]?.contents ?? "",
            );
            setTitle(
              templates[
                value as ResultOf<
                  typeof EMAIL_TEMPLATE_CONFIGURATIONS_QUERY
                >["emailTemplateConfigurations"][number]["triggerEvent"]
              ]?.title ?? "",
            );
          },
        },
        title: {
          type: "INPUT",
          label: "Title",
          yupConfig: yup.string().required(),
          defaultValue: data?.emailTemplate?.title,
          inputType: "text",
          value: title,
          onChange(value) {
            setTitle(value);
          },
        },
        default: {
          type: "CHECKBOX",
          label: "Default",
          yupConfig: yup.boolean().required(),
          defaultValue: data?.emailTemplate?.default === true,
        },
        availableVariables: {
          type: "DYNAMIC",
          label: "Available Variables",
          yupConfig: yup.mixed(),
          element: () =>
            selectedTriggerEventConfig ? (
              <Stack gap={4}>
                <Text
                  size="xs"
                  tt="uppercase"
                  fw={600}
                  c="cyan"
                  style={{ letterSpacing: "1px" }}
                >
                  Available Variables
                </Text>
                {selectedTriggerEventConfig.availableVariables.map(
                  (variable: any) => (
                    <Code
                      key={variable.name}
                      style={{ cursor: "default", display: "block" }}
                    >
                      {`{{${variable.name}}}`}
                      <Text size="xs" c="dimmed" component="span" ml="xs">
                        {variable.description}
                      </Text>
                    </Code>
                  ),
                )}
              </Stack>
            ) : (
              <Text size="sm" c="dimmed">
                Select a trigger event to see available variables
              </Text>
            ),
        },
        contents: {
          type: "CODE_EDITOR",
          label: "Email contents",
          language: "html",
          height: "100%",
          yupConfig: yup.string().required(),
          defaultValue: data?.emailTemplate?.contents,
          value: contents,
          onChange(value) {
            setContents(value);
          },
        },
        preview: {
          type: "PREVIEW_PANEL" as const,
          label: "Preview",
          watchField: "contents",
          yupConfig: yup.mixed(),
        },
      }}
      onSubmit={async ({ availableVariables, ...input }) => {
        if (templateId) {
          await updateMutation({
            variables: { id: templateId, input },
          }).then((data) => {
            if (data?.error) {
              throw new Error(data.error.message);
            }

            notifications.show({
              title: "Success",
              message: "Email template updated",
              color: "green",
              autoClose: 3000,
            });
          });
        } else {
          await createMutation({
            variables: { input },
          }).then((data) => {
            if (data?.error) {
              throw new Error(data.error.message);
            }

            const id = data?.data?.emailTemplateCreate?.id ?? "";
            notifications.show({
              title: "Success",
              message: "Email template created",
              color: "green",
              autoClose: 3000,
            });
            navigate(id);
          });
        }
      }}
    />
  );
};
